import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { State } from 'src/app/app.reducer';
import { CouponEmdType, OptionType } from 'src/app/core/enums/enums';
import { ContractError } from 'src/app/core/models/models';
import { Operation } from 'src/app/core/models/operation-log.model';
import { GenericError } from 'src/app/layout/actions/layout.actions';
import { Remarks } from 'src/app/core/models/remarks.model';
import { IInfantReassignmentControl } from 'src/app/core/models/infant-reassignment-control.model';
import { StepResult } from 'src/app/core/models/Step-Result.model';
import { PnrUtil } from '../utils/pnr.util';
import { PenaltyFareBasisMachineLearning } from 'src/app/core/models/cancellation.model';
import {
  PassengerQuote,
  PnrAvailableOption,
  PnrInfo,
  QuoteRequest,
  WaiverData,
  Passenger,
  Segment,
  TicketHistory,
  ISRequestValidation,
  IAdditionalFlightInfo,
  IOAGFLTStatus,
  IRemarkSavedOnProccess
} from 'src/app/core/models/itinerary.model';
import {
  OperationLogActions,
  TicketHistoryActions,
  ItineraryHistoryActions,
  PnrActions,
  TabActions,
  remarksActions,
  seatActions,
  infantControlActions
} from '../actions';
import {
  ICancelSeatResponseBody,
  ReservationSeat,
  ReserveSeatConfirmation,
  SeatMap
} from 'src/app/core/models/seat.model';
import {
  IVoidTransactionState,
  voidTransactionStates
} from 'src/app/core/models/void-transaction.model';

export interface PnrState {
  pnrInfo: PnrInfo;
  pnrAvailableOption: PnrAvailableOption;
  selectedPassengersHasInvalidCoupons: boolean;
  remarksResult: string;
  remarks: Remarks;
  remarkSavedOnProccess: IRemarkSavedOnProccess;
  selectedPassengersQuote: PassengerQuote[];
  selectedSegments: number[];
  overrideSegments: number[];
  voluntaryOverrideSegments: number[];
  loading: boolean;
  error: ContractError;
  quoteRequest: QuoteRequest;
  operations: Operation[];
  loadingActions: boolean;
  actionActive: string;
  history: string;
  itineraryLogHistory: string;
  airCompanyNotes: string;
  itineraryHistory: StepResult[];
  ticketHistory: TicketHistory[];
  activeTab: string;
  exceptionMode: boolean;
  showExceptionSwitch: boolean;
  waiverData: WaiverData;
  commission: number;
  seatMap: SeatMap;
  reserveSeatResult: ReserveSeatConfirmation;
  cancelSeatResult: ICancelSeatResponseBody;
  showSeatMap: boolean;
  seatListToReserve: ReservationSeat[];
  loadingReserveSeat: boolean;
  loadingCancelSeat: boolean;
  isErrorinGetSeatMap: boolean;
  isErrorinReserveSeatMap: boolean;
  errorReserveSeatMap: string;
  errorSeatMap: string;
  infantReasignationControl: IInfantReassignmentControl;
  voidTransaction: IVoidTransactionState;
  penaltiesFareBasis: PenaltyFareBasisMachineLearning;
  rulesFarebasis: PenaltyFareBasisMachineLearning | ContractError;
  loadingRules: boolean;
  sRequestValidationResponse: ISRequestValidation[];
  sRequestValidationError: boolean;
  receivedFrom: string;
  additionalFlightInfo: IAdditionalFlightInfo;
  OAGFLTStatus: IOAGFLTStatus;
  logInLog: {
    logInLogRegistered: boolean;
    isError: boolean;
    error: string;
  };
}

export const initialPnrState: PnrState = {
  pnrInfo: null,
  pnrAvailableOption: null,
  selectedPassengersHasInvalidCoupons: false,
  remarksResult: null,
  remarks: null,
  remarkSavedOnProccess: {
    closePNR: false
  },
  selectedPassengersQuote: null,
  selectedSegments: null,
  overrideSegments: [],
  voluntaryOverrideSegments: [],
  loading: false,
  loadingActions: false,
  error: null,
  quoteRequest: null,
  operations: null,
  actionActive: '',
  history: '',
  itineraryLogHistory: '',
  airCompanyNotes: '',
  itineraryHistory: null,
  ticketHistory: [],
  activeTab: 'info',
  exceptionMode: false,
  showExceptionSwitch: true,
  waiverData: null,
  commission: 0,
  seatMap: null,
  reserveSeatResult: null,
  cancelSeatResult: null,
  showSeatMap: false,
  seatListToReserve: [],
  loadingReserveSeat: false,
  loadingCancelSeat: false,
  isErrorinGetSeatMap: false,
  isErrorinReserveSeatMap: false,
  errorReserveSeatMap: '',
  errorSeatMap: '',
  infantReasignationControl: { adultsInCharge: [], infantsReassigned: [] },
  voidTransaction: {
    currentProccess: voidTransactionStates.initial,
    error: null
  },
  penaltiesFareBasis: null,
  rulesFarebasis: null,
  loadingRules: false,
  sRequestValidationResponse: null,
  sRequestValidationError: false,
  receivedFrom: null,
  additionalFlightInfo: null,
  OAGFLTStatus: {
    isSuccess: false,
    isError: false,
    data: null,
    error: ''
  },
  logInLog: {
    logInLogRegistered: false,
    isError: false,
    error: ''
  }
};

const pnrReducer = createReducer(
  initialPnrState,
  on(PnrActions.ClearPnrInfo, () => ({
    ...initialPnrState,
    pnrInfo: null
  })),

  // #region App state reset
  on(PnrActions.ClearPnrSuccess, (state) => ({
    pnrInfo: state.pnrInfo,
    pnrAvailableOption: null,
    selectedPassengersHasInvalidCoupons: false,
    remarksResult: null,
    remarks: null,
    remarkSavedOnProccess: {
      closePNR: false
    },
    selectedPassengersQuote: null,
    selectedSegments: null,
    overrideSegments: [],
    voluntaryOverrideSegments: [],
    loading: false,
    loadingActions: false,
    error: null,
    quoteRequest: null,
    operations: null,
    actionActive: '',
    history: '',
    itineraryLogHistory: '',
    airCompanyNotes: '',
    itineraryHistory: null,
    ticketHistory: [],
    activeTab: 'info',
    exceptionMode: false,
    showExceptionSwitch: true,
    waiverData: null,
    commission: 0,
    seatMap: null,
    reserveSeatResult: null,
    cancelSeatResult: null,
    seatListToReserve: [],
    showSeatMap: false,
    loadingReserveSeat: false,
    loadingCancelSeat: false,
    isErrorinReserveSeatMap: false,
    errorReserveSeatMap: '',
    isErrorinGetSeatMap: false,
    errorSeatMap: '',
    infantReasignationControl: { adultsInCharge: [], infantsReassigned: [] },
    isAirline: null,
    voidTransaction: {
      currentProccess: voidTransactionStates.initial,
      error: null
    },
    penaltiesFareBasis: null,
    rulesFarebasis: null,
    loadingRules: false,
    sRequestValidationResponse: null,
    sRequestValidationError: false,
    receivedFrom: null,
    additionalFlightInfo: null,
    OAGFLTStatus: {
      isSuccess: false,
      isError: false,
      data: null,
      error: ''
    },
    logInLog: state?.logInLog
  })),
  // #endregion

  on(PnrActions.LoadPnr, (state) => ({
    ...initialPnrState,
    loadingReserveSeat: false,
    reserveSeatResult: null,
    showSeatMap: false,
    loading: true,
    error: null
  })),

  /**
   * !Important:
   * *Info: This action also resets the App initial state
   */
  // #region App state reset
  on(PnrActions.LoadPnrSuccess, (state, { pnrInfo }) => ({
    ...initialPnrState,
    pnrInfo: PnrUtil.addSpecialRequestTempId(pnrInfo),
    loading: false,
    activeTab: 'info',
    logInLog: state?.logInLog
  })),
  // #endregion

  on(
    PnrActions.SetSelectedPassengersHasInvalidCoupons,
    (state, { selectedPassengersHasInvalidCoupons }) => ({
      ...state,
      selectedPassengersHasInvalidCoupons
    })
  ),

  on(PnrActions.UpdatePnrInfo, (state, { pnrInfo }) => ({
    ...state,
    pnrInfo
  })),

  on(PnrActions.LoadSelectedPassengers, (state, { selectedPassengersQuote }) => ({
    ...state,
    selectedPassengersQuote
  })),

  on(PnrActions.LoadSelectedSegments, (state, { selectedSegments }) => ({
    ...state,
    selectedSegments
  })),

  on(PnrActions.GoToInvoluntaryChange, (state, { quoteRequest }) => ({
    ...state,
    quoteRequest,
    error: null
  })),

  on(PnrActions.LoadAvailableOption, (state, { quoteRequest }) => ({
    ...state,
    quoteRequest: {
      ...quoteRequest,
      selectedPassengers: null,
      passengers: quoteRequest.selectedPassengers
    },
    loading: true,
    loadingActions: true,
    error: null
  })),

  on(
    PnrActions.LoadAvailableOptionSuccess,
    (state, { pnrAvailableOption, machineLearningEnabled }) => ({
      ...state,
      pnrAvailableOption,
      loadingActions: machineLearningEnabled,
      loading: false
    })
  ),

  on(GenericError, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    loadingActions: false,
    pnrAvailableOption: null,
    showExceptionSwitch: true
  })),

  on(PnrActions.CloseModal, (state) => ({
    ...state,
    error: null
  })),

  on(PnrActions.CloseModalAction, (state) => ({
    ...state,
    pnrAvailableOption: null
  })),

  on(OperationLogActions.LoadOperationsSuccess, (state, { operations }) => ({
    ...state,
    operations,
    activeTab: 'log'
  })),

  on(OperationLogActions.LoadPNRHistorySuccess, (state, { history }) => ({
    ...state,
    history
  })),

  on(OperationLogActions.LoadAirCompanyNotesSuccess, (state, { airCompanyNotes }) => ({
    ...state,
    airCompanyNotes,
    activeTab: 'airCompanyNotes'
  })),

  on(OperationLogActions.LoadPNRItineraryHistorySuccess, (state, { itineraryHistory }) => ({
    ...state,
    itineraryHistory,
    activeTab: 'history'
  })),

  on(ItineraryHistoryActions.LoadItineraryHistorySuccess, (state, { itineraryLogHistory }) => ({
    ...state,
    itineraryLogHistory,
    activeTab: 'itineraryHistory'
  })),

  on(PnrActions.SetReceivedFrom, (state, { receivedFrom }) => ({
    ...state,
    pnrInfo: {
      ...state.pnrInfo,
      receivedFrom
    },
    receivedFrom
  })),

  on(PnrActions.RemoveReceivedFrom, (state) => ({
    ...state,
    receivedFrom: null
  })),

  on(TicketHistoryActions.LoadTicketHistorySuccess, (state, { ticketHistory }) => ({
    ...state,
    ticketHistory,
    activeTab: 'ticketHistory'
  })),

  on(PnrActions.ChangeToPnrSplitted, (state, { cancellationQuote }) => ({
    ...state,
    quoteRequest: {
      ...state.quoteRequest,
      pnr: cancellationQuote.pnr
    },
    pnrInfo: {
      ...state.pnrInfo,
      code: cancellationQuote.pnr
    }
  })),

  on(PnrActions.SplitPnrRequest, (state, { quoteRequest }) => ({
    ...state,
    quoteRequest
  })),

  on(PnrActions.SplitPnrSuccess, (state, { pnr }) => ({
    ...state,
    quoteRequest: {
      ...state.quoteRequest,
      pnr: pnr.code
    },
    pnrInfo: pnr
  })),

  on(TabActions.RouteTabTo, (state, { activeTab }) => ({
    ...state,
    activeTab
  })),

  on(PnrActions.ChooseActionMode, (state, { actionActive }) => ({
    ...state,
    actionActive,
    exceptionMode: state.exceptionMode
  })),

  on(TabActions.ChangeExceptionMode, (state, { exceptionMode }) => ({
    ...state,
    exceptionMode
  })),

  on(TabActions.HideOrShowExceptionSwitch, (state, { show }) => ({
    ...state,
    showExceptionSwitch: show
  })),

  on(TabActions.SaveWaiverData, (state, { waiverData }) => ({
    ...state,
    waiverData
  })),

  on(PnrActions.UpdateOverrideSegments, (state, { overrideSegments }) => ({
    ...state,
    overrideSegments,
    loading: false
  })),

  on(PnrActions.setCommision, (state, { value }) => ({
    ...state,
    commission: value
  })),

  on(
    remarksActions.CreateRemarksFromComponentSuccess,
    (state, { remarksResult, savedFromProccess }) => {
      const remarkSavedOnProccess = {
        ...state.remarkSavedOnProccess
      };

      if (savedFromProccess) {
        switch (savedFromProccess) {
          case OptionType.ClosePNR:
            remarkSavedOnProccess.closePNR = true;
            break;

          default:
            break;
        }
      }

      return {
        ...state,
        remarksResult,
        remarkSavedOnProccess
      };
    }
  ),

  on(remarksActions.SaveRemarksFromComponentSuccess, (state, { remarksResult }) => ({
    ...state,
    remarks: remarksResult
  })),

  on(seatActions.showSeatMap, (state) => ({
    ...state,
    showSeatMap: true,
    isErrorinSeatMap: false,
    loadingReserveSeat: false
  })),

  on(seatActions.hideSeatMap, (state) => ({
    ...state,
    showSeatMap: false
  })),

  on(seatActions.GetSeatMapSuccess, (state, { seatMap }) => ({
    ...state,
    seatMap
  })),

  on(seatActions.GetSeatMapFailed, (state, { error }) => ({
    ...state,
    isErrorinGetSeatMap: true,
    errorSeatMap: error
  })),

  on(seatActions.SaveSeatSelection, (state, { seatListToReserve }) => ({
    ...state,
    seatListToReserve: [...state.seatListToReserve, seatListToReserve]
  })),

  on(seatActions.RemoveSeatSelection, (state, { seatNumber, seatName, segment }) => ({
    ...state,
    seatListToReserve: state.seatListToReserve.filter(
      (op) =>
        op !==
        state.seatListToReserve.find(
          (op) =>
            op.seatSelect.number === seatNumber &&
            op.nameSelect.nameNumber === seatName &&
            op.segmentSelect.number === segment
        )
    )
  })),

  on(seatActions.ReserveSeat, (state) => ({
    ...state,
    isErrorinRerieveSeatMap: false,
    loadingReserveSeat: true
  })),

  on(seatActions.ReserveSeatSuccess, (state, { reserveSeatResult }) => ({
    ...state,
    showSeatMap: false,
    loadingReserveSeat: false,
    isErrorinReserveSeatMap: false,
    reserveSeatResult,
    seatListToReserve: []
  })),

  on(seatActions.ReserveSeatFailed, (state, { error }) => ({
    ...state,
    showSeatMap: false,
    loadingReserveSeat: false,
    isErrorinReserveSeatMap: true,
    errorReserveSeatMap: error
  })),

  on(seatActions.CancelSeat, (state) => ({
    ...state,
    isErrorinSeatMap: false,
    loadingCancelSeat: true
  })),

  on(seatActions.CancelSeatSuccess, (state, { cancelSeatResult }) => ({
    ...state,
    showSeatMap: false,
    loadingCancelSeat: false,
    reserveSeatResult: null,
    cancelSeatResult
  })),

  on(infantControlActions.SetInfantReassignmentControl, (state, { adultsInCharge, infantID }) => ({
    ...state,
    infantReasignationControl: {
      adultsInCharge: [...state.infantReasignationControl.adultsInCharge, adultsInCharge],
      infantsReassigned: [...state.infantReasignationControl.infantsReassigned, infantID]
    }
  })),

  on(infantControlActions.ClearInfantReassignmentControl, (state, { adultsInCharge, infantID }) => {
    const currentAdultsInChargestate = state.infantReasignationControl.adultsInCharge;
    let indexAdults = -1;
    currentAdultsInChargestate.forEach((adults, index) => {
      if (
        adults.fromAdult === adultsInCharge.fromAdult &&
        adults.toAdult === adultsInCharge.toAdult
      ) {
        indexAdults = index;
      }
    });
    if (indexAdults > -1) {
      currentAdultsInChargestate.splice(indexAdults, 1);
    }

    const currentInfantsReassignedState = state.infantReasignationControl.infantsReassigned;
    const indexInfants = currentInfantsReassignedState.indexOf(infantID);
    if (indexInfants > -1) {
      currentInfantsReassignedState.splice(indexInfants, 1);
    }

    return {
      ...state,
      infantReasignationControl: {
        adultsInCharge: currentAdultsInChargestate,
        infantsReassigned: currentInfantsReassignedState
      }
    };
  }),

  on(PnrActions.UpdateAncillariesValidation, (state, { ancillaries }) => ({
    ...state,
    pnrInfo: PnrUtil.addAncillaryValidation(state.pnrInfo, ancillaries)
  })),

  on(PnrActions.ForceRefreshSesionAndVoid, (state) => ({
    ...state,
    loading: true
  })),

  on(PnrActions.VoidTransaction, (state) => ({
    ...state,
    loading: true,
    voidTransaction: {
      ...state.voidTransaction,
      currentProccess: voidTransactionStates.makingQuery
    }
  })),

  on(PnrActions.VoidTransactionSuccess, (state) => ({
    ...state,
    loading: false,
    voidTransaction: {
      ...state.voidTransaction,
      currentProccess: voidTransactionStates.success
    }
  })),

  on(PnrActions.VoidTransactionFail, (state, { error, message }) => ({
    ...state,
    loading: false,
    voidTransaction: {
      currentProccess: voidTransactionStates.error,
      error: {
        info: error,
        message
      }
    }
  })),

  on(PnrActions.LoadPenaltyTextAISuccess, (state, { penalty }) => ({
    ...state,
    penaltiesFareBasis: penalty
  })),

  on(PnrActions.LoadRulesIA, (state) => ({
    ...state,
    rulesFarebasis: null,
    loadingRules: true
  })),

  on(PnrActions.LoadRulesIASuccess, (state, { penalty }) => ({
    ...state,
    rulesFarebasis: penalty,
    loadingRules: false
  })),

  on(PnrActions.LoadRulesIAFail, (state, { error }) => ({
    ...state,
    rulesFarebasis: error,
    loadingRules: false
  })),

  on(PnrActions.SRequestValidationPerFlightSuccess, (state, { response }) => ({
    ...state,
    sRequestValidationResponse: response,
    sRequestValidationError: false,
    loadingRules: false
  })),

  on(PnrActions.SRequestValidationPerFlightFailed, (state) => ({
    ...state,
    sRequestValidationError: true,
    loadingRules: false
  })),

  on(PnrActions.ResetSRequestValidationPerFlight, (state) => ({
    ...state,
    sRequestValidationResponse: null,
    sRequestValidationError: false,
    loadingRules: false
  })),

  on(PnrActions.LoadOfficeCountry, (state, { officeCountry }) => ({
    ...state,
    pnrInfo: {
      ...state.pnrInfo,
      officeCountry
    }
  })),

  on(PnrActions.UpdatePassengersQuote, (state, { selectedPassengersQuote }) => ({
    ...state,
    selectedPassengersQuote
  })),

  on(PnrActions.GetAdditionalFlightInfo, (state) => ({
    ...state,
    loading: true,
    additionalFlightInfo: null
  })),

  on(PnrActions.GetAdditionalFlightInfoSuccess, (state, { additionalFlightInfo }) => ({
    ...state,
    loading: false,
    additionalFlightInfo
  })),

  on(PnrActions.SetVoluntaryOverrideSegments, (state, { voluntaryOverrideSegments }) => ({
    ...state,
    voluntaryOverrideSegments
  })),

  on(PnrActions.GetOAGFLTStatus, (state) => ({
    ...state,
    loading: true,
    OAGFLTStatus: {
      isSuccess: false,
      isError: false,
      data: null,
      error: ''
    }
  })),

  on(PnrActions.GetOAGFLTStatusSuccess, (state, { OAGFLTStatus }) => ({
    ...state,
    loading: false,
    OAGFLTStatus: {
      isSuccess: true,
      isError: false,
      data: OAGFLTStatus,
      error: ''
    }
  })),

  on(PnrActions.GetOAGFLTStatusFailed, (state, { error }) => ({
    ...state,
    loading: false,
    OAGFLTStatus: {
      isSuccess: false,
      isError: true,
      data: null,
      error
    }
  })),

  on(PnrActions.RegisterUserLogInLog, (state) => ({
    ...state,
    loading: true
  })),

  on(PnrActions.RegisterUserLogInLogSuccess, (state) => ({
    ...state,
    loading: false,
    logInLog: {
      logInLogRegistered: true,
      isError: false,
      error: ''
    }
  })),

  on(PnrActions.RegisterUserLogInLogFailed, (state, { error }) => ({
    ...state,
    loading: false,
    logInLog: {
      logInLogRegistered: false,
      isError: true,
      error
    }
  }))
);

export function PnrReducer(state: PnrState | undefined, action: Action): PnrState {
  return pnrReducer(state, action);
}

export const selectPnrState = createFeatureSelector<State, PnrState>(OptionType.SearchResult);

export const getPnrInfo = createSelector(selectPnrState, (state) => state.pnrInfo);

export const getIsExpired = createSelector(selectPnrState, (state) => state?.pnrInfo?.isExpired);

export const getError = createSelector(selectPnrState, (state) => state.error);

export const getLoading = createSelector(selectPnrState, (state) => state.loading);

export const getLoadingActions = createSelector(selectPnrState, (state) => state.loadingActions);

export const getLoadingRules = createSelector(selectPnrState, (state) => state.loadingRules);

export const getQuoteRequest = createSelector(selectPnrState, (state) => state.quoteRequest);

export const getAvailableOption = createSelector(
  selectPnrState,
  (state) => state.pnrAvailableOption
);

export const getSelectedPassengersHasInvalidCoupons = createSelector(
  selectPnrState,
  (state) => state.selectedPassengersHasInvalidCoupons
);

export const getPnr = createSelector(selectPnrState, (state) => state.pnrInfo.code);

export const getItinerary = createSelector(
  selectPnrState,
  (state) => state.pnrInfo && state.pnrInfo.itinerary
);

export const getOperations = createSelector(selectPnrState, (state) => state.operations);

export const getPNRHistory = createSelector(selectPnrState, (state) => state.history);

export const getAirCompanyNotes = createSelector(selectPnrState, (state) => state.airCompanyNotes);

export const getPNRItineraryHistory = createSelector(
  selectPnrState,
  (state) => state.itineraryHistory
);

export const getTicketHistory = createSelector(selectPnrState, (state) => state.ticketHistory);

export const getItineraryHistory = createSelector(
  selectPnrState,
  (state) => state.itineraryLogHistory
);

export const getActionActive = createSelector(selectPnrState, (state) => state.actionActive);

export const getSelectedPassengersQuote = createSelector(
  selectPnrState,
  (state) => state.selectedPassengersQuote
);

export const getSelectedSegments = createSelector(
  selectPnrState,
  (state) => state.selectedSegments
);

export const getActiveTab = createSelector(selectPnrState, (state) => state.activeTab);

export const getExceptionMode = createSelector(selectPnrState, (state) => state.exceptionMode);

export const getShowExceptionSwitch = createSelector(
  selectPnrState,
  (state) => state.showExceptionSwitch
);

export const somePassengerHasTicket = createSelector(selectPnrState, (state) => {
  const passenger = state.pnrInfo.passengers.find(
    (pass) =>
      pass?.ticket !== undefined &&
      pass?.ticket !== null &&
      pass?.ticket?.number !== undefined &&
      pass?.ticket?.number !== null
  );
  return !!passenger ? true : false;
});

const getAllFlights = (segments: Segment[]): number[] => {
  return segments.reduce(
    (final, seg) =>
      final.concat(seg.flights.reduce((flights, flight) => flights.concat(flight.index), [])),
    []
  );
};

const buildPrimaryQuoteRequestForInvoluntary = (state: PnrState): QuoteRequest => {
  return {
    pnrOption: state?.quoteRequest?.pnrOption || OptionType.InvoluntaryChange,
    pnr: state.pnrInfo && state.pnrInfo.code ? state.pnrInfo.code : null,
    country: state.pnrInfo && state.pnrInfo.countryCode ? state.pnrInfo.countryCode : null,
    gds: state.pnrInfo && state.pnrInfo.gds ? state.pnrInfo.gds : null,
    flights:
      state.pnrInfo && state.pnrInfo.itinerary
        ? getAllFlights(state.pnrInfo.itinerary.segments)
        : null,
    isException: state.exceptionMode,
    passengers: state.selectedPassengersQuote
      ? state.selectedPassengersQuote
      : state.pnrInfo.passengers.map((pass: Passenger) => ({
          passengerType: pass.type,
          ticketNumber: pass.ticket.number
        })),
    comission: state.commission,
    officeId: state.pnrInfo && state.pnrInfo.officeId,
    receivedFrom: state.receivedFrom
  };
};

const buildSecundaryQuoteRequestForInvoluntary = (state: PnrState): QuoteRequest => {
  return {
    pnrOption: OptionType.InvoluntaryChange,
    pnr: state.pnrInfo && state.pnrInfo.code ? state.pnrInfo.code : null,
    country: state.pnrInfo && state.pnrInfo.countryCode ? state.pnrInfo.countryCode : null,
    gds: state.pnrInfo && state.pnrInfo.gds ? state.pnrInfo.gds : null,
    flights:
      state.pnrInfo && state.pnrInfo.itinerary
        ? getAllFlights(state.pnrInfo.itinerary.segments)
        : null,
    isException: state.exceptionMode,
    passengers: null,
    comission: state.commission,
    receivedFrom: state.receivedFrom
  };
};

export const selectQuoteRequestForInvoluntary = createSelector(selectPnrState, (state) => {
  if (state && state.pnrInfo !== null && state.selectedPassengersQuote !== null) {
    const primaryQuote = buildPrimaryQuoteRequestForInvoluntary(state);
    return primaryQuote;
  } else {
    const secundaryQuote = buildSecundaryQuoteRequestForInvoluntary(state);
    return secundaryQuote;
  }
});

export const selectQuoteRequest = createSelector(selectPnrState, (state) => {
  return {
    pnrOption: state.actionActive,
    pnr: state.pnrInfo.code,
    country: state.pnrInfo.countryCode,
    gds: state.pnrInfo.gds,
    flights: state.selectedSegments,
    isException: false,
    passengers: state.selectedPassengersQuote,
    receivedFrom: state.receivedFrom
  } as QuoteRequest;
});

export const hasSplit = createSelector(selectPnrState, (state) =>
  state.selectedPassengersQuote
    ? state.pnrInfo.passengers.length > state.selectedPassengersQuote.length
    : false
);

export const getOverrideSegments = createSelector(
  selectPnrState,
  (state) => state.overrideSegments
);

export const getSomePassengerHasAncillaryTypeS = createSelector(selectPnrState, (state) =>
  state.pnrInfo?.passengers
    .filter((passenger) =>
      state.selectedPassengersQuote
        ?.map((passengerQuote) => passengerQuote.id)
        ?.includes(passenger.id)
    )
    .filter((passenger) =>
      passenger.specialRequests.some(
        (specialRequest) =>
          specialRequest.coupons.length > 0 &&
          specialRequest.coupons.some((coupon) => coupon.emdtype === CouponEmdType.S)
      )
    )
);

export const selectVoidTransactionState = createSelector(
  selectPnrState,
  (state) => state.voidTransaction
);

export const getPenaltiesFareBasis = createSelector(
  selectPnrState,
  (state) => state.penaltiesFareBasis
);

export const getRulesFarebasis = createSelector(selectPnrState, (state) => state.rulesFarebasis);

export const getPassengersWithMco = createSelector(selectPnrState, (state) => {
  const { mcoMessages, passengers } = state.pnrInfo;
  return passengers.map((passenger) => ({
    id: passenger.id,
    name: `${passenger.firstName} ${passenger.lastName}`,
    ticketNumber: passenger?.ticket?.number,
    messages: mcoMessages?.filter((message) => message.reference === passenger.id),
    isOpen: false
  }));
});

export const getSRequestValidationResponse = createSelector(
  selectPnrState,
  (state) => state.sRequestValidationResponse
);

export const getSRequestValidationError = createSelector(
  selectPnrState,
  (state) => state.sRequestValidationError
);

export const selectAdditionalFlightInfo = createSelector(
  selectPnrState,
  (state) => state.additionalFlightInfo
);

export const selectVoluntaryOverrideSegments = createSelector(
  selectPnrState,
  (state) => state.voluntaryOverrideSegments
);

export const selectOAGFLTStatus = createSelector(selectPnrState, (state) => state.OAGFLTStatus);

export const selectLogInLogState = createSelector(selectPnrState, (state) => state.logInLog);
